/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
  Modal,
  Stack,
  Text,
  Textarea,
  Button,
  Group,
  Alert,
  Paper,
  Badge,
  LoadingOverlay,
  SegmentedControl,
  Title,
  Box,
  Grid,
  Accordion,
  Divider,
  Progress
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconAlertCircle,
  IconClipboardCheck,
  IconList,
  IconCalendar,
  IconEye,
  IconFileSpreadsheet
} from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import {
  yearlyCalendarSchedulePlanService,
  ApprovalData,
  YearlyCalendarSchedulePlan,
  YearlyCalendarSchedulePlanDetail
} from 'services/yearlyCalendar/schedulePlanService';
import Dates from 'components/Dates';
import ReadOnlyCalendarScheduleView from './ReadOnlyCalendarScheduleView';

interface BulkApprovalModalProps {
  opened: boolean;
  onClose: () => void;
  selectedPlans: YearlyCalendarSchedulePlan[];
  onSuccess: () => void;
}

interface ScheduleCourse {
  msLCMSCourseID: string;
  courseName: string;
  courseCode: string;
  learningHours: number;
  deliveryType?: string;
  planningStart: string | null;
  planningEnd: string | null;
  trLCMSPlanningCoursesID: string;
}

const BulkApprovalModal: React.FC<BulkApprovalModalProps> = ({
  opened,
  onClose,
  selectedPlans,
  onSuccess
}) => {
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentProcessing, setCurrentProcessing] = useState<string>('');
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [selectedPlanDetails, setSelectedPlanDetails] = useState<
    YearlyCalendarSchedulePlanDetail[]
  >([]);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [importPreviewOpen, setImportPreviewOpen] = useState(false);
  const [importedRows, setImportedRows] = useState<any[]>([]);

  // Fetch detailed data for selected plans
  const {
    data: planDetails,
    isLoading: isLoadingDetails,
    error: detailsError
  } = useQuery({
    queryKey: ['bulkSchedulePlanDetails', selectedPlans.map((p) => p.msLCMSPlanningID)],
    queryFn: async () => {
      const promises = selectedPlans.map((plan) =>
        yearlyCalendarSchedulePlanService.getSchedulePlanDetail(plan.msLCMSPlanningID)
      );
      const results = await Promise.allSettled(promises);

      return results
        .filter(
          (
            result
          ): result is PromiseFulfilledResult<{
            message: string;
            data: YearlyCalendarSchedulePlanDetail;
          }> => result.status === 'fulfilled'
        )
        .map((result) => result.value.data);
    },
    enabled: opened && selectedPlans.length > 0,
    refetchOnWindowFocus: false
  });

  // Fetch holiday calendar
  const { data: holidayData, isLoading: isLoadingHolidays } = useQuery({
    queryKey: ['holidayCalendar', new Date().getFullYear()],
    queryFn: () => yearlyCalendarSchedulePlanService.getHolidayCalendar(new Date().getFullYear()),
    enabled: opened,
    refetchOnWindowFocus: false
  });

  // Update plan details when data is fetched
  useEffect(() => {
    if (planDetails) {
      setSelectedPlanDetails(planDetails);
    }
  }, [planDetails]);

  // Convert all courses from all plans to the format expected by ReadOnlyCalendarScheduleView
  const allCourses = useMemo((): ScheduleCourse[] => {
    const courses: ScheduleCourse[] = [];

    selectedPlanDetails.forEach((plan) => {
      if (plan.courses) {
        plan.courses.forEach((course) => {
          courses.push({
            msLCMSCourseID: course.msLCMSCourseID,
            courseName: course.courseName,
            courseCode: course.courseCode,
            learningHours: course.learningHours,
            deliveryType: course.deliveryType,
            planningStart: course.planningStart,
            planningEnd: course.planningEnd,
            trLCMSPlanningCoursesID: course.trLCMSPlanningCoursesID
          });
        });
      }
    });

    return courses;
  }, [selectedPlanDetails]);

  // Build export data and trigger download
  const handleExportSelectedPlansToExcel = () => {
    try {
      // Helper function to safely decode HTML entities and strip HTML tags
      const decodeHTML = (html: string): string => {
        if (!html) return '';

        // Create a temporary DOM element to safely decode HTML entities
        const tempElement = document.createElement('div');
        tempElement.innerHTML = html;

        // Get text content (this automatically decodes entities and strips tags)
        return (tempElement.textContent || tempElement.innerText || '').replace(/\s+/g, ' ').trim();
      };

      // Group courses by planID + courseCode so we can produce Raw Data, General View and per-month sheets
      type Group = {
        planID: string;
        planName: string;
        courseCode: string;
        courseName: string;
        recs: any[];
      };

      const groupedMap = new Map<string, Group>();

      selectedPlanDetails.forEach((planDetail) => {
        const plan = selectedPlans.find((p) => p.msLCMSPlanningID === planDetail.msLCMSPlanningID);
        const planName = plan?.planningName || planDetail.planningName || '';
        const planID = plan?.msLCMSPlanningID || '';

        (planDetail.courses || []).forEach((course) => {
          const courseCode = course.courseCode || course.msLCMSCourseID || '';
          const key = `${planID}|${courseCode}`;
          if (!groupedMap.has(key)) {
            groupedMap.set(key, {
              planID,
              planName,
              courseCode,
              courseName: course.courseName || '-',
              recs: []
            });
          }

          groupedMap.get(key)!.recs.push({
            ...course,
            planID,
            planName
          });
        });
      });

      const groupsArray = Array.from(groupedMap.values()).sort(
        (a, b) =>
          (a.planName || '').localeCompare(b.planName || '') ||
          (a.courseName || '').localeCompare(b.courseName || '')
      );

      const rawData: any[] = [];
      // prepare range for months
      let minDate: Date | null = null;
      let maxDate: Date | null = null;

      for (const g of groupsArray) {
        g.recs.sort((r1, r2) => {
          const t1 = r1.planningStart ? new Date(r1.planningStart).getTime() : 0;
          const t2 = r2.planningStart ? new Date(r2.planningStart).getTime() : 0;
          return (t1 || 0) - (t2 || 0);
        });

        for (let i = 0; i < g.recs.length; i++) {
          const course = g.recs[i];
          // accumulate min/max planning dates
          if (course.planningStart) {
            const s = new Date(course.planningStart);
            if (!isNaN(s.getTime())) {
              if (!minDate || s < minDate) minDate = s;
              if (!maxDate || s > maxDate) maxDate = s;
            }
          }
          if (course.planningEnd) {
            const e = new Date(course.planningEnd);
            if (!isNaN(e.getTime())) {
              if (!minDate || e < minDate) minDate = e;
              if (!maxDate || e > maxDate) maxDate = e;
            }
          }

          rawData.push({
            'No.': rawData.length + 1,
            'Plan Name': g.planName,
            Batch: i + 1,
            'Course Name': g.courseName,
            'Course Code': g.courseCode,
            School: course.schoolName || '-',
            'Delivery Type': course.deliveryType || '-',
            'Mode of Delivery': course.modeOfDelivery || '-',
            'Course Status': course.statusCourse || '-',
            'Learning Hours': course.learningHours || 0,
            'Planning Start': course.planningStart || '',
            'Planning End': course.planningEnd || '',
            'Assessment Start': course.assessmentStartDate || '',
            'Assessment End': course.assessmentEndDate || '',
            'Learning Objectives': decodeHTML(course.learningObjectives || ''),
            Notes: course.notes || '-',
            'Approval Decision (Approved/Rejected)': course.approvalDecision || 'Pending',
            'Approval Notes': course.approvalDecisionNotes || '-'
          });
        }
      }

      // if no date range found, use current year
      const now = new Date();
      const startDate = minDate || new Date(now.getFullYear(), 0, 1);
      const endDate = maxDate || new Date(now.getFullYear(), 11, 31);

      // build months list
      const months: { year: number; month: number }[] = [];
      const cursor = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
      while (cursor <= endDate) {
        months.push({ year: cursor.getFullYear(), month: cursor.getMonth() });
        cursor.setMonth(cursor.getMonth() + 1);
      }

      const wb = XLSX.utils.book_new();

      const wsRaw = XLSX.utils.json_to_sheet(rawData);
      wsRaw['!cols'] = [
        { wch: 5 }, // No.
        { wch: 30 }, // Plan Name
        { wch: 8 }, // Batch
        { wch: 30 }, // Course Name
        { wch: 15 }, // Course Code
        { wch: 25 }, // School
        { wch: 12 }, // Delivery Type
        { wch: 15 }, // Mode of Delivery
        { wch: 12 }, // Course Status
        { wch: 10 }, // Learning Hours
        { wch: 15 }, // Planning Start
        { wch: 15 }, // Planning End
        { wch: 15 }, // Assessment Start
        { wch: 15 }, // Assessment End
        { wch: 50 }, // Learning Objectives (wider for decoded text)
        { wch: 30 }, // Notes
        { wch: 20 }, // Approval Decision
        { wch: 30 } // Approval Notes
      ];
      XLSX.utils.book_append_sheet(wb, wsRaw, 'Raw Data');

      // General View (month columns)
      const monthHeaders = months.map((m) =>
        new Date(m.year, m.month, 1).toLocaleString('en-US', { month: 'short', year: 'numeric' })
      );
      const generalHeader = ['Plan Name', 'Course Name', ...monthHeaders];
      const generalAoa: any[][] = [generalHeader];

      for (const g of groupsArray) {
        const row: any[] = [g.planName, g.courseName + (g.courseCode ? ` (${g.courseCode})` : '')];
        const monthBuckets: Set<number>[] = Array.from(
          { length: months.length },
          () => new Set<number>()
        );

        for (const rec of g.recs) {
          const pushDate = (dStr?: string | null) => {
            if (!dStr) return;
            const d = new Date(dStr);
            if (isNaN(d.getTime())) return;
            const mi = months.findIndex(
              (mm) => mm.year === d.getFullYear() && mm.month === d.getMonth()
            );
            if (mi >= 0) monthBuckets[mi].add(d.getDate());
          };

          if (rec.planningStart && rec.planningEnd) {
            const s = new Date(rec.planningStart);
            const e = new Date(rec.planningEnd);
            if (!isNaN(s.getTime()) && !isNaN(e.getTime())) {
              const iter = new Date(s);
              while (iter <= e) {
                pushDate(iter.toISOString());
                iter.setDate(iter.getDate() + 1);
              }
            } else {
              pushDate(rec.planningStart);
            }
          } else {
            pushDate(rec.planningStart);
          }
        }

        for (let mi = 0; mi < months.length; mi++) {
          const days = Array.from(monthBuckets[mi]).sort((a, b) => a - b);
          row.push(days.length ? days.join(', ') : '');
        }

        generalAoa.push(row);
      }

      const wsGeneral = XLSX.utils.aoa_to_sheet(generalAoa);
      wsGeneral['!cols'] = [{ wch: 30 }, { wch: 40 }, ...monthHeaders.map(() => ({ wch: 18 }))];
      XLSX.utils.book_append_sheet(wb, wsGeneral, 'General View');

      // Per-month sheets (week buckets)
      for (const m of months) {
        const year = m.year;
        const monthIndex = m.month;
        const monthStart = new Date(year, monthIndex, 1);
        const monthEnd = new Date(year, monthIndex + 1, 0);
        const lastDay = monthEnd.getDate();
        const numberOfWeeks = Math.ceil(lastDay / 7);

        const header = ['Plan Name', 'Course Name'];
        for (let w = 1; w <= numberOfWeeks; w++) header.push(`Week ${w}`);
        const aoa: any[][] = [header];

        for (const g of groupsArray) {
          const row: any[] = [];
          row.push(g.planName);
          row.push(g.courseName + (g.courseCode ? ` (${g.courseCode})` : ''));

          const weekBuckets: Set<number>[] = Array.from(
            { length: numberOfWeeks },
            () => new Set<number>()
          );

          for (const rec of g.recs) {
            const addDate = (dStr?: string | null) => {
              if (!dStr) return;
              const d = new Date(dStr);
              if (isNaN(d.getTime())) return;
              if (d.getFullYear() === year && d.getMonth() === monthIndex) {
                const day = d.getDate();
                const wk = Math.floor((day - 1) / 7);
                if (weekBuckets[wk]) weekBuckets[wk].add(day);
              }
            };

            if (rec.planningStart && rec.planningEnd) {
              const s = new Date(rec.planningStart);
              const e = new Date(rec.planningEnd);
              if (!isNaN(s.getTime()) && !isNaN(e.getTime())) {
                const iter = new Date(s);
                while (iter <= e) {
                  addDate(iter.toISOString());
                  iter.setDate(iter.getDate() + 1);
                }
              } else {
                addDate(rec.planningStart);
              }
            } else {
              addDate(rec.planningStart);
            }
          }

          for (let w = 0; w < numberOfWeeks; w++) {
            const days = Array.from(weekBuckets[w]).sort((a, b) => a - b);
            row.push(days.length ? days.join(', ') : '');
          }

          aoa.push(row);
        }

        const wsMonth = XLSX.utils.aoa_to_sheet(aoa);
        wsMonth['!cols'] = [
          { wch: 30 },
          { wch: 40 },
          ...Array.from({ length: numberOfWeeks }, () => ({ wch: 12 }))
        ];
        const sheetName = `${monthStart.toLocaleString('en-US', { month: 'short' })}_${year}`;
        XLSX.utils.book_append_sheet(wb, wsMonth, sheetName);
      }

      const dateRange = `${startDate.toISOString().split('T')[0]}_to_${endDate.toISOString().split('T')[0]}`;
      const defaultFilename = `bulk_schedule_plans_calendarview_${dateRange}.xlsx`;
      XLSX.writeFile(wb, defaultFilename);
    } catch (err) {
      notifications.show({
        title: 'Export Failed',
        message: 'Failed to export selected plans',
        color: 'red'
      });
    }
  };

  // Import and parse Excel file, then map decisions to plan/course IDs and run approvals
  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement> | null) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    setIsImporting(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);

      // Prefer Raw Data sheet if present
      const sheetName = workbook.SheetNames.find((n) => n === 'Raw Data') || workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName!];
      const parsed: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      // helpers to normalize headers and decisions
      const normalizeHeader = (h: string) =>
        h
          .toString()
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '');

      const normalizeDecision = (v: any) => {
        const s = (v || '').toString().trim().toLowerCase();
        if (!s) return '';
        if (['approved', 'approve', 'a', 'true', 'yes', 'y'].includes(s)) return 'Approved';
        if (['rejected', 'reject', 'r', 'false', 'no', 'n'].includes(s)) return 'Rejected';
        return s.charAt(0).toUpperCase() + s.slice(1);
      };

      // map row keys to normalized keys for more robust parsing
      const normalizedRows = parsed.map((row) => {
        const out: any = {};
        Object.keys(row).forEach((k) => {
          out[normalizeHeader(k)] = row[k];
        });
        return out;
      });

      const decisions: any[] = [];

      normalizedRows.forEach((r) => {
        // support multiple header variants
        const trKey =
          r['trlcmsplanningcoursesid'] ||
          r['trlcmsplanningcourses_id'] ||
          r['tr_lcms_planning_courses_id'];
        const planKey = r['plan_id'] || r['planid'] || r['plan'];
        const courseCodeKey = r['course_code'] || r['coursecode'] || r['course'];
        const decisionKey =
          r['approval_decision'] ||
          r['approval_decision_approved_rejected'] ||
          r['decision'] ||
          r['status'];
        const notesKey = r['approval_notes'] || r['notes'] || r['note'];

        const decision = normalizeDecision(decisionKey);
        const notesCell = notesKey || '-';
        if (!decision) return; // skip if no decision

        decisions.push({
          trLCMSPlanningCoursesID: trKey || '',
          planID: planKey || '',
          courseCode: courseCodeKey || '',
          decision,
          notes: notesCell || '-'
        });
      });

      if (decisions.length === 0) {
        notifications.show({
          title: 'Import Result',
          message: 'No valid approval decisions found in the file',
          color: 'yellow'
        });
        return;
      }

      // build preview items by mapping decisions to selected plans/courses
      const previewItems: any[] = [];

      decisions.forEach((d) => {
        let matched = false;

        if (d.trLCMSPlanningCoursesID) {
          for (const plan of selectedPlanDetails) {
            const course = plan.courses?.find(
              (c) => c.trLCMSPlanningCoursesID === d.trLCMSPlanningCoursesID
            );
            if (course) {
              previewItems.push({
                msLCMSPlanningID: plan.msLCMSPlanningID,
                planName: plan.planningName,
                courseCode: course.courseCode,
                courseName: course.courseName,
                decision: d.decision,
                notes: d.notes
              });
              matched = true;
              break;
            }
          }
        }

        if (!matched && d.planID && d.courseCode) {
          for (const plan of selectedPlanDetails) {
            if (plan.msLCMSPlanningID === d.planID || plan.planningName === d.planID) {
              const course = plan.courses?.find((c) => c.courseCode === d.courseCode);
              if (course) {
                previewItems.push({
                  msLCMSPlanningID: plan.msLCMSPlanningID,
                  planName: plan.planningName,
                  courseCode: course.courseCode,
                  courseName: course.courseName,
                  decision: d.decision,
                  notes: d.notes
                });
                matched = true;
                break;
              }
            }
          }
        }

        if (!matched) {
          // include as unmatched preview item so user can see it
          previewItems.push({
            msLCMSPlanningID: d.planID || 'N/A',
            planName: d.planID || 'Unknown',
            courseCode: d.courseCode || 'Unknown',
            courseName: '',
            decision: d.decision,
            notes: d.notes,
            unmatched: true
          });
        }
      });

      setImportedRows(previewItems);
      setImportPreviewOpen(true);
    } catch (err) {
      notifications.show({
        title: 'Import Failed',
        message: 'Failed to parse the Excel file',
        color: 'red'
      });
    } finally {
      setIsImporting(false);
      setIsSubmitting(false);
      setCurrentProcessing('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Bulk approval mutation
  const bulkApprovalMutation = useMutation({
    mutationFn: async (approvalData: { status: 'Approved' | 'Rejected'; notes: string }) => {
      const results = [];
      let successCount = 0;
      let errorCount = 0;

      for (const plan of selectedPlans) {
        try {
          setCurrentProcessing(plan.planningName);

          const approval: ApprovalData = {
            msLCMSPlanningID: plan.msLCMSPlanningID,
            status: approvalData.status,
            notes: approvalData.notes
          };

          await yearlyCalendarSchedulePlanService.approveSchedulePlan(approval);
          results.push({ plan, success: true });
          successCount++;
        } catch (error) {
          results.push({ plan, success: false, error });
          errorCount++;
        }
      }

      return { results, successCount, errorCount };
    },
    onSuccess: (data, variables) => {
      const { successCount, errorCount } = data;

      if (errorCount === 0) {
        notifications.show({
          title: 'Bulk Approval Successful',
          message: `All ${successCount} schedule plans have been ${variables.status.toLowerCase()} successfully`,
          color: variables.status === 'Approved' ? 'green' : 'orange',
          icon: variables.status === 'Approved' ? <IconCheck size={16} /> : <IconX size={16} />
        });
      } else {
        notifications.show({
          title: 'Bulk Approval Completed with Errors',
          message: `${successCount} successful, ${errorCount} failed. Please check individual plans.`,
          color: 'yellow',
          icon: <IconAlertCircle size={16} />
        });
      }

      handleClose();
      onSuccess();
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Bulk Approval Failed',
        message: error?.response?.data?.message || 'Failed to process bulk approval',
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
      setIsSubmitting(false);
      setCurrentProcessing('');
    }
  });

  const handleBulkApproval = (status: 'Approved' | 'Rejected') => {
    if (!notes.trim()) {
      notifications.show({
        title: 'Required Field',
        message: 'Please provide notes for the approval decision',
        color: 'yellow',
        icon: <IconAlertCircle size={16} />
      });
      return;
    }

    if (notes.trim().length < 10) {
      notifications.show({
        title: 'Notes Too Short',
        message: 'Please provide at least 10 characters in the notes',
        color: 'yellow',
        icon: <IconAlertCircle size={16} />
      });
      return;
    }

    setIsSubmitting(true);
    bulkApprovalMutation.mutate({
      status,
      notes: notes.trim()
    });
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setNotes('');
      setCurrentProcessing('');
      setViewMode('list');
      setSelectedPlanDetails([]);
      setImportedRows([]);
      setImportPreviewOpen(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      onClose();
    }
  };

  // Calculate statistics
  const totalCourses = selectedPlanDetails.reduce(
    (sum, plan) => sum + (plan.courses?.length || 0),
    0
  );
  const scheduledCourses = selectedPlanDetails.reduce(
    (sum, plan) =>
      sum + (plan.courses?.filter((c) => c.planningStart && c.planningEnd).length || 0),
    0
  );

  const isLoadingData = isLoadingDetails || isLoadingHolidays;
  const holidays = holidayData?.data || [];

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={null}
      size='90%'
      padding='xl'
      radius='lg'
      centered
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={false}
      overlayProps={{
        backgroundOpacity: 0.5,
        blur: 8
      }}
    >
      <LoadingOverlay
        visible={isSubmitting}
        overlayProps={{ backgroundOpacity: 0.8 }}
        loaderProps={{
          children: currentProcessing ? (
            <Stack align='center' gap='sm'>
              <Text fw={500}>Processing...</Text>
              <Text size='sm' c='dimmed' ta='center'>
                {currentProcessing}
              </Text>
            </Stack>
          ) : (
            'Processing bulk approval...'
          )
        }}
      />

      <Stack gap='xl'>
        {/* Header */}
        <Box>
          <Group gap='sm' mb='xs'>
            <IconClipboardCheck size={24} color='#667eea' />
            <Title order={2} fw={600}>
              Bulk Schedule Plan Approval
            </Title>
          </Group>
          <Text size='sm' c='dimmed' mb='md'>
            Review the selected plans and switch between List and Calendar views.
          </Text>

          <Group gap='md' wrap='wrap' align='stretch'>
            <Paper withBorder radius='md' p='md' style={{ minWidth: 180 }}>
              <Text size='xs' c='dimmed' tt='uppercase' fw={600}>
                Plans selected
              </Text>
              <Text size='xl' fw={700} c='blue'>
                {selectedPlans.length}
              </Text>
            </Paper>

            {totalCourses > 0 && (
              <>
                <Paper withBorder radius='md' p='md' style={{ minWidth: 180 }}>
                  <Text size='xs' c='dimmed' tt='uppercase' fw={600}>
                    Total courses
                  </Text>
                  <Text size='xl' fw={700} c='green'>
                    {totalCourses}
                  </Text>
                </Paper>

                <Paper withBorder radius='md' p='md' style={{ minWidth: 260, flex: '1 1 260px' }}>
                  <Group justify='space-between' align='flex-end'>
                    <div>
                      <Text size='xs' c='dimmed' tt='uppercase' fw={600}>
                        Overall progress
                      </Text>
                      <Text size='sm' c='dimmed'>
                        {scheduledCourses}/{totalCourses} scheduled
                      </Text>
                    </div>
                    <Text
                      fw={700}
                      c={
                        (totalCourses > 0
                          ? Math.round((scheduledCourses / totalCourses) * 100)
                          : 0) === 100
                          ? 'green'
                          : 'orange'
                      }
                    >
                      {totalCourses > 0 ? Math.round((scheduledCourses / totalCourses) * 100) : 0}%
                    </Text>
                  </Group>
                  <Progress
                    value={
                      totalCourses > 0 ? Math.round((scheduledCourses / totalCourses) * 100) : 0
                    }
                    color={
                      (totalCourses > 0
                        ? Math.round((scheduledCourses / totalCourses) * 100)
                        : 0) === 100
                        ? 'green'
                        : (totalCourses > 0
                              ? Math.round((scheduledCourses / totalCourses) * 100)
                              : 0) > 0
                          ? 'orange'
                          : 'gray'
                    }
                    mt='sm'
                    radius='xl'
                    size='sm'
                  />
                </Paper>
              </>
            )}
          </Group>

          <Divider my='md' />

          <Paper withBorder radius='md' p='xs' style={{ background: '#fbfcff' }}>
            <SegmentedControl
              fullWidth
              size='md'
              radius='md'
              value={viewMode}
              onChange={(value) => setViewMode(value as 'list' | 'calendar')}
              data={[
                {
                  label: (
                    <Group gap='xs'>
                      <IconList size={14} />
                      <Text size='sm'>List</Text>
                    </Group>
                  ),
                  value: 'list'
                },
                {
                  label: (
                    <Group gap='xs'>
                      <IconCalendar size={14} />
                      <Text size='sm'>Calendar</Text>
                    </Group>
                  ),
                  value: 'calendar'
                }
              ]}
            />
          </Paper>
          {/* Export / Import controls */}
          <Group mt='sm'>
            <Button
              leftSection={<IconFileSpreadsheet size={14} />}
              onClick={() => handleExportSelectedPlansToExcel()}
              variant='default'
            >
              Export Selected to Excel
            </Button>

            <input
              ref={(el) => (fileInputRef.current = el)}
              type='file'
              accept='.xlsx,.xls'
              style={{ display: 'none' }}
              onChange={(e) => handleImportFile(e)}
            />

            <Button
              leftSection={<IconClipboardCheck size={14} />}
              onClick={() => fileInputRef.current?.click()}
              variant='outline'
              disabled={isSubmitting || isImporting}
            >
              Import Approvals (Excel)
            </Button>
          </Group>
        </Box>

        {/* Error Alert */}
        {detailsError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title='Error Loading Details'
            color='red'
            variant='light'
          >
            Failed to load some schedule plan details. You can still proceed with approval.
          </Alert>
        )}

        {/* Content */}
        {isLoadingData ? (
          <Box py='xl'>
            <Text ta='center' c='dimmed'>
              Loading schedule details...
            </Text>
          </Box>
        ) : viewMode === 'calendar' ? (
          <Paper p='lg' withBorder radius='md'>
            <Stack gap='md'>
              <Group justify='space-between'>
                <Text fw={500} size='lg'>
                  Schedule Overview
                </Text>
              </Group>
              <ReadOnlyCalendarScheduleView courses={allCourses} />
            </Stack>
          </Paper>
        ) : (
          <Stack gap='md'>
            {selectedPlans.map((plan) => {
              const planDetail = selectedPlanDetails.find(
                (p) => p.msLCMSPlanningID === plan.msLCMSPlanningID
              );
              const planCourses = (planDetail?.courses || []).slice().sort((a, b) => {
                let ta = Number.MAX_SAFE_INTEGER;
                if (a.planningStart) {
                  const da = new Date(a.planningStart);
                  if (!isNaN(da.getTime())) ta = da.getTime();
                }

                let tb = Number.MAX_SAFE_INTEGER;
                if (b.planningStart) {
                  const db = new Date(b.planningStart);
                  if (!isNaN(db.getTime())) tb = db.getTime();
                }

                if (ta !== tb) return ta - tb;

                const na = (a.courseName || '').toString();
                const nb = (b.courseName || '').toString();
                return na.localeCompare(nb);
              });
              const scheduledCount = planCourses.filter(
                (c) => c.planningStart && c.planningEnd
              ).length;

              return (
                <Paper key={plan.msLCMSPlanningID} p='lg' withBorder radius='md'>
                  <Grid>
                    <Grid.Col span={8}>
                      <Stack gap='xs'>
                        <Group justify='space-between'>
                          <Text fw={600} size='lg'>
                            {plan.planningName}
                          </Text>
                          <Badge
                            variant='light'
                            color={
                              plan.statusApproval.toLocaleUpperCase() === 'PLANNED'
                                ? 'yellow'
                                : 'blue'
                            }
                          >
                            {plan.statusApproval.toUpperCase() === 'PLANNED'
                              ? 'UNPLANNED'
                              : plan.statusApproval || 'Unknown'}
                          </Badge>
                        </Group>

                        {plan.planningDescription && (
                          <Text size='sm' c='dimmed'>
                            {plan.planningDescription}
                          </Text>
                        )}

                        <Group gap='lg' mt='xs'>
                          <Box>
                            <Text size='xs' c='dimmed' tt='uppercase' fw={500}>
                              Planning Period
                            </Text>
                            <Text size='sm'>
                              <Dates date={plan.planningStart} /> -{' '}
                              <Dates date={plan.planningEnd} />
                            </Text>
                          </Box>

                          {plan.programType && (
                            <Box>
                              <Text size='xs' c='dimmed' tt='uppercase' fw={500}>
                                Program Type
                              </Text>
                              <Text size='sm'>{plan.programType}</Text>
                            </Box>
                          )}
                        </Group>
                      </Stack>
                    </Grid.Col>

                    <Grid.Col span={4}>
                      <Stack gap='sm' align='flex-end'>
                        <Group gap='lg'>
                          <Box ta='right'>
                            <Text size='xs' c='dimmed' tt='uppercase' fw={500}>
                              Courses
                            </Text>
                            <Text size='xl' fw={600} c='blue'>
                              {planCourses.length}
                            </Text>
                          </Box>

                          <Box ta='right'>
                            <Text size='xs' c='dimmed' tt='uppercase' fw={500}>
                              Scheduled
                            </Text>
                            <Text
                              size='xl'
                              fw={600}
                              c={scheduledCount === planCourses.length ? 'green' : 'orange'}
                            >
                              {scheduledCount}/{planCourses.length}
                            </Text>
                          </Box>
                        </Group>

                        {planCourses.length > 0 && (
                          <Badge
                            variant='light'
                            color={scheduledCount === planCourses.length ? 'green' : 'orange'}
                            size='md'
                          >
                            {Math.round((scheduledCount / planCourses.length) * 100)}% Complete
                          </Badge>
                        )}
                      </Stack>
                    </Grid.Col>
                  </Grid>

                  {planCourses.length > 0 && (
                    <Accordion mt='md' variant='contained'>
                      <Accordion.Item value={`courses-${plan.msLCMSPlanningID}`}>
                        <Accordion.Control>
                          <Group gap='xs'>
                            <IconEye size={16} />
                            <Text>View Courses ({planCourses.length})</Text>
                          </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Grid>
                            {planCourses.map((course) => {
                              const sameCourses = planCourses
                                .filter((c) => c.msLCMSCourseID === course.msLCMSCourseID)
                                .slice()
                                .sort((a, b) => {
                                  const ta = a.planningStart
                                    ? new Date(a.planningStart).getTime()
                                    : Number.MAX_SAFE_INTEGER;
                                  const tb = b.planningStart
                                    ? new Date(b.planningStart).getTime()
                                    : Number.MAX_SAFE_INTEGER;
                                  return ta - tb;
                                });

                              const batchIndex = sameCourses.findIndex(
                                (c) => c.trLCMSPlanningCoursesID === course.trLCMSPlanningCoursesID
                              );
                              const batchNumber = batchIndex >= 0 ? batchIndex + 1 : 1;

                              return (
                                <Grid.Col key={course.trLCMSPlanningCoursesID} span={6}>
                                  <Paper p='sm' style={{ background: '#f8f9fa' }}>
                                    <Group justify='space-between'>
                                      <Box style={{ flex: 1 }}>
                                        <Text fw={500} size='sm'>
                                          {course.courseName}
                                        </Text>
                                        <Text size='xs' c='dimmed'>
                                          {course.courseCode}
                                        </Text>

                                        <Group gap='xs' mt={6}>
                                          <Badge size='xs' variant='light'>
                                            Batch {batchNumber}
                                          </Badge>

                                          {course.planningStart && course.planningEnd && (
                                            <Text size='xs' c='blue'>
                                              <Dates date={course.planningStart} /> -{' '}
                                              <Dates date={course.planningEnd} />
                                            </Text>
                                          )}
                                        </Group>

                                        <Text size='xs' c='dimmed' mt={6}>
                                          {course.schoolName ? `${course.schoolName} • ` : ''}
                                          {course.deliveryType || '-'}
                                        </Text>
                                      </Box>
                                      <Stack gap={4} align='flex-end'>
                                        <Badge size='xs' variant='light'>
                                          {course.learningHours}h
                                        </Badge>
                                        <Badge
                                          size='xs'
                                          variant='light'
                                          color={
                                            course.planningStart && course.planningEnd
                                              ? 'green'
                                              : 'orange'
                                          }
                                        >
                                          {course.planningStart && course.planningEnd
                                            ? 'Scheduled'
                                            : 'Unscheduled'}
                                        </Badge>
                                      </Stack>
                                    </Group>
                                  </Paper>
                                </Grid.Col>
                              );
                            })}
                          </Grid>
                        </Accordion.Panel>
                      </Accordion.Item>
                    </Accordion>
                  )}
                </Paper>
              );
            })}
          </Stack>
        )}

        {/* Warning Alert */}
        <Alert icon={<IconAlertCircle size={16} />} color='blue' variant='light'>
          <Text size='sm' fw={500} mb='xs'>
            Bulk Approval Action
          </Text>
          <Text size='sm'>
            You are about to process {selectedPlans.length} schedule plan(s). This action will apply
            the same approval decision and notes to all selected plans. This action cannot be
            undone.
          </Text>
        </Alert>

        {/* Progress Info during submission */}
        {isSubmitting && currentProcessing && (
          <Alert icon={<IconAlertCircle size={16} />} color='blue' variant='light'>
            <Text size='sm'>
              Processing: <strong>{currentProcessing}</strong>
            </Text>
          </Alert>
        )}

        {/* Notes Input */}
        <Stack gap='xs'>
          <Text size='sm' fw={500}>
            Approval Notes{' '}
            <Text component='span' c='red'>
              *
            </Text>
          </Text>
          <Textarea
            placeholder='Enter your notes regarding this bulk approval decision...'
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            minRows={4}
            disabled={isSubmitting}
            error={!notes.trim() && notes.length > 0 ? 'Notes are required' : null}
          />
          <Text size='xs' c='dimmed'>
            Minimum 10 characters required. These notes will be applied to all selected plans.
          </Text>
        </Stack>

        {/* Action Buttons */}
        <Group justify='space-between'>
          <Button variant='outline' onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>

          <Group>
            <Button
              color='red'
              leftSection={<IconX size={16} />}
              onClick={() => handleBulkApproval('Rejected')}
              disabled={isSubmitting || !notes.trim() || notes.trim().length < 10}
              loading={isSubmitting && bulkApprovalMutation.variables?.status === 'Rejected'}
            >
              Reject All ({selectedPlans.length})
            </Button>

            <Button
              color='green'
              leftSection={<IconCheck size={16} />}
              onClick={() => handleBulkApproval('Approved')}
              disabled={isSubmitting || !notes.trim() || notes.trim().length < 10}
              loading={isSubmitting && bulkApprovalMutation.variables?.status === 'Approved'}
            >
              Approve All ({selectedPlans.length})
            </Button>
          </Group>
        </Group>
      </Stack>

      {/* Import Preview Modal */}
      <Modal
        opened={importPreviewOpen}
        onClose={() => setImportPreviewOpen(false)}
        title='Import Preview'
        size='60%'
      >
        <Stack>
          <Text size='sm' c='dimmed'>
            Review imported approval decisions. Unmatched rows are listed and won't be applied.
          </Text>

          <div style={{ maxHeight: 360, overflow: 'auto' }}>
            {importedRows.map((row, idx) => (
              <Paper
                key={idx}
                p='sm'
                mb='xs'
                withBorder
                radius='md'
                style={{ opacity: row.unmatched ? 0.6 : 1 }}
              >
                <Group style={{ justifyContent: 'space-between' }} align='center'>
                  <div>
                    <Text fw={600}>{row.planName}</Text>
                    <Text size='xs' c='dimmed'>
                      {row.courseName} {row.courseCode ? `(${row.courseCode})` : ''}
                    </Text>
                  </div>

                  <div style={{ textAlign: 'right' }}>
                    <Text fw={700} c={row.decision === 'Approved' ? 'green' : 'red'}>
                      {row.decision}
                    </Text>
                    <Text size='xs' c='dimmed'>
                      {row.notes || '-'}
                    </Text>
                    {row.unmatched && (
                      <Text size='xs' c='orange'>
                        Unmatched - will be skipped
                      </Text>
                    )}
                  </div>
                </Group>
              </Paper>
            ))}
          </div>

          <Group style={{ justifyContent: 'flex-end' }}>
            <Button variant='outline' onClick={() => setImportPreviewOpen(false)}>
              Cancel
            </Button>
            <Button
              color='green'
              onClick={async () => {
                // Confirm and apply approvals
                setImportPreviewOpen(false);
                const toApply = importedRows.filter((r) => !r.unmatched);
                if (toApply.length === 0) {
                  notifications.show({
                    title: 'Nothing to Apply',
                    message: 'No matched rows to apply',
                    color: 'yellow'
                  });
                  return;
                }

                setIsSubmitting(true);
                let applied = 0;
                let failed = 0;

                for (const item of toApply) {
                  setCurrentProcessing(item.msLCMSPlanningID || item.planName || '');
                  try {
                    const approval: ApprovalData = {
                      msLCMSPlanningID: item.msLCMSPlanningID,
                      status: item.decision === 'Approved' ? 'Approved' : 'Rejected',
                      notes: item.notes || notes || '-'
                    };

                    await yearlyCalendarSchedulePlanService.approveSchedulePlan(approval);
                    applied++;
                  } catch (err) {
                    failed++;
                  }
                }

                setIsSubmitting(false);
                setCurrentProcessing('');

                if (failed === 0) {
                  notifications.show({
                    title: 'Import Applied',
                    message: `All ${applied} approvals applied`,
                    color: 'green'
                  });
                } else {
                  notifications.show({
                    title: 'Import Applied with Errors',
                    message: `${applied} applied, ${failed} failed`,
                    color: 'yellow'
                  });
                }

                onSuccess();
                handleClose();
              }}
            >
              Apply Approvals
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Modal>
  );
};

export default BulkApprovalModal;
